<template>
  <div class="my-2 mx-0.5">
    <bvc-textarea
      v-if="useTextarea"
      ref="input"
      v-bind="bindings"
      :rows="lines"
      :style="[{ minWidth, textTransform: currentValue && textTransform }]"
      v-model:model-value="currentValue"
      :data-testid="testId"
      :clearable="showClear"
      @clear="onClear"
      @focus="focus"
      @blur="blur"
      @keyup="onKeyup"
      @click="onInputClick"
    />
    <bvc-input
      v-else
      ref="input"
      v-bind="bindings"
      :style="[{ minWidth, textTransform: currentValue && textTransform }]"
      :data-testid="testId"
      v-model:model-value="currentValue"
      :clearable="showClear"
      @clear="onClear"
      @focus="focus"
      @blur="blur"
      @keyup="onKeyup"
      @click="onInputClick"
    >
      <template v-if="$slots.left" #addonBefore>
        <slot name="left" v-bind="slotBindings" />
      </template>
      <template v-if="$slots.right" #addonAfter>
        <slot name="right" v-bind="slotBindings" />
      </template>
    </bvc-input>
    <div
      v-if="useTextarea && maxCharacters"
      class="text-end"
      data-testid="char-count"
    >
      {{ charCount }}
    </div>
  </div>
</template>

<script lang="ts">
/*
 *
 * b-input component
 *
 */
import { defineComponent, type PropType } from 'vue'
import { is } from 'ramda'

import Input from '../../mixins/input'
import InputSize from '../../mixins/input-size'
import VModelInterface from '../../mixins/v-model-interface'

import { BInput as BvcInput, BTextarea as BvcTextarea } from '@booksy-com/vue-components-core'

export const THEME_TYPES = [
  'default',
  'blank',
  'blank--no-h-padding',
  'filled',
]

const SUPPORTED_TYPES = [
  'text',
  'url',
  'email',
  'password',
  'tel',
  'search',
  'number',
]

const INPUTMODE_TYPES = [
  'text',
  'numeric',
  'tel',
  'decimal',
  'email',
  'url',
  'search',
  'none',
]

const AUTOCOMPLETE_TYPES = [
  'on',
  'off',
  'name',
  'email',
  'tel',
  'url',
  'photo',
  'none',
  'tel-national',
  'address-line1',
  'address-line2',
  'street-address',
]

const TEXT_STYLES = [
  'none',
  'lowercase',
  'capitalize',
  'uppercase',
]

type InputMixinHost = {
  input__bindings: Record<string, unknown>
  readonly: boolean
  disabled: boolean
  invalid: boolean
  warning: boolean
  size: string
  value?: string
  name: string
}

type InputElementRef = HTMLInputElement | HTMLTextAreaElement | {
  focus: () => void
}

export default defineComponent({

  emits: [
    'blur',
    'change',
    'clear',
    'click',
    'focus',
    'input',
    'keyup',
    'update:modelValue',
  ],
  name: 'b-input',

  mixins: [
    Input,
    InputSize,
    VModelInterface,
  ],

  props: {

    type: {
      type: String,
      default: SUPPORTED_TYPES[0],
      validator: (value: string) => SUPPORTED_TYPES.includes(value),
    },

    label: {
      type: String,
      default: '',
    },

    lines: {
      type: Number,
      default: 1,
    },

    placeholder: {
      type: String as PropType<string | null>,
      default: null,
    },

    autocomplete: {
      type: String,
      default: AUTOCOMPLETE_TYPES[0],
      validator: (value: string) => AUTOCOMPLETE_TYPES.includes(value),
    },

    inputmode: {
      type: String,
      default: INPUTMODE_TYPES[0],
      validator: (value: string) => INPUTMODE_TYPES.includes(value),
    },

    textTransform: {
      type: String,
      default: TEXT_STYLES[0],
      validator: (value: string) => TEXT_STYLES.includes(value),
    },

    // enforces active state
    active: {
      type: Boolean as PropType<boolean | null>,
      default: null,
    },

    autosize: {
      type: Boolean as PropType<boolean | null>,
      default: null,
    },

    hasClear: {
      type: Boolean,
      default: true,
    },

    minWidth: {
      type: Number,
      default: 0,
    },

    maxLength: {
      type: Number as PropType<number | null>,
      default: null,
    },

    maxCharacters: Number,

    // prevent losing focus on clicks (e.g. in bvc-select)
    keepFocus: {
      type: Boolean,
      default: false,
    },

    role: {
      type: String as PropType<string | null>,
      default: null,
    },

    modelValue: {
      type: String,
      default: undefined,
    },
  },

  components: {
    BvcInput,
    BvcTextarea,
  },

  data (): { focused: boolean } {
    return {
      focused: false,
    }
  },

  computed: {
    useTextarea (): boolean {
      return !!(this.autosize || this.lines > 1)
    },

    bindings (): Record<string, unknown> {
      const {
        input__bindings,
        size,
        invalid,
      } = this as typeof this & InputMixinHost
      // @see input mixin
      const bindings = input__bindings
      bindings.type = this.type
      bindings.role = this.role
      bindings.autocomplete = this.autocomplete
      bindings.inputmode = this.inputmode
      bindings.maxlength = this.maxLength
      bindings.placeholder = this.placeholder || this.label
      bindings.size = size
      bindings.state = invalid ? 'invalid' : 'default'

      return bindings
    },

    /*
     * bindings for left & right slot
     * @return Object
     */
    slotBindings (): {
      readonly: boolean
      disabled: boolean
      invalid: boolean
      warning: boolean
      focused: boolean
      filled: boolean
    } {
      const { readonly, disabled, invalid, warning } = this as typeof this & InputMixinHost
      return {
        readonly,
        disabled,
        invalid,
        warning,
        focused: this.isFocused,
        filled: this.isFilled,
      }
    },

    isFocused (): boolean {
      if (is(Boolean, this.active)) {
        return this.active as boolean
      }

      return this.focused
    },

    isFilled (): boolean {
      const { value } = this as typeof this & { value?: string | number }

      if (value === 0) return true
      return !!this.currentValue
    },

    showClear (): boolean {
      const { disabled, readonly } = this as typeof this & InputMixinHost
      /*
       * shortcut: if "disabled" or "readonly" value can not be
       *           changed, including "clear"
       */
      if (disabled || readonly) return false

      if (this.type === 'search' && this.isFilled) return true

      return this.hasClear && this.isFilled
    },

    charCount (): string {
      return `${this.currentValue?.length || 0}/${this.maxCharacters}`
    },

    maxCharactersReached (): boolean {
      return (this.currentValue?.length || 0) >= (this.maxCharacters ?? 0)
    },

    testId (): string | undefined {
      const { name } = this as typeof this & InputMixinHost
      return name || undefined
    },

    currentValue: {
      get (): string {
        const { value } = this as typeof this & InputMixinHost
        return value || this.modelValue || ''
      },
      set (value: string) {
        this.$emit('update:modelValue', value)
        this.$emit('input', value)
        this.$emit('change', value)
      },
    },
  },

  methods: {

    /*
     *
     * public interface
     *
     */
    focus (e?: Event) {
      const { disabled, readonly } = this as typeof this & InputMixinHost
      if (disabled || readonly) return false
      this.focused = true
      ;(this.$refs.input as InputElementRef).focus()
      this.$emit('focus', e)
    },

    blur (e?: Event) {
      this.focused = false
      this.$nextTick(() => {
        this.$emit('blur', e)
      })
    },

    /*
     *
     * private
     *
     */
    onKeyup (e: KeyboardEvent) {
      this.$emit('keyup', e)
    },

    onClear () {
      this.$emit('clear')
      this.currentValue = ''
    },

    onInputClick (event: MouseEvent) {
      this.$emit('click', event)
      if (this.keepFocus) {
        event.stopPropagation()
      }
    },
  },
})
</script>
